import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { X, CheckCircle2, AlertTriangle, AlertOctagon } from "lucide-react";
import { cn } from "@/lib/utils";

const ToastContext = createContext(null);
const MAX_STACK = 3;
const DEFAULT_DURATION = 5000;

/**
 * <ToastProvider> mounts a viewport in the bottom-right corner and exposes
 * `useToast()` returning { show, success, warning, danger }.
 * Auto-dismiss after 5s, pausable on hover, stack of up to 3.
 */
export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const seq = useRef(0);

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const show = useCallback((opts) => {
    const id = ++seq.current;
    const toast = {
      id,
      message: opts?.message ?? "",
      variant: opts?.variant ?? "default",
      duration: opts?.duration ?? DEFAULT_DURATION,
    };
    setToasts((prev) => [...prev, toast].slice(-MAX_STACK));
    return id;
  }, []);

  const api = {
    show: (message, opts) =>
      typeof message === "string"
        ? show({ ...opts, message })
        : show(message),
    success: (message, opts) => show({ ...opts, message, variant: "success" }),
    warning: (message, opts) => show({ ...opts, message, variant: "warning" }),
    danger: (message, opts) => show({ ...opts, message, variant: "danger" }),
    dismiss,
  };

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        aria-live="polite"
        aria-atomic="true"
        className="pointer-events-none fixed bottom-6 right-6 z-[200] flex w-[calc(100vw-48px)] max-w-[360px] flex-col items-end gap-2"
        data-testid="toast-viewport"
      >
        {toasts.map((t) => (
          <ToastItem key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast must be used within <ToastProvider>");
  }
  return ctx;
}

const VARIANT_CLS = {
  default: "bg-frac-black text-frac-white border-hairline",
  success: "bg-[#1E6B3C] text-white border-transparent",
  warning: "bg-[#8A5A00] text-white border-transparent",
  danger: "bg-[#A11C1C] text-white border-transparent",
};

const VARIANT_ICON = {
  default: null,
  success: CheckCircle2,
  warning: AlertTriangle,
  danger: AlertOctagon,
};

function ToastItem({ toast, onDismiss }) {
  const remainingRef = useRef(toast.duration);
  const startRef = useRef(Date.now());
  const timerRef = useRef(null);

  const start = useCallback(
    (ms) => {
      clearTimeout(timerRef.current);
      startRef.current = Date.now();
      timerRef.current = setTimeout(onDismiss, ms);
    },
    [onDismiss],
  );

  const pause = useCallback(() => {
    clearTimeout(timerRef.current);
    remainingRef.current -= Date.now() - startRef.current;
    if (remainingRef.current < 0) remainingRef.current = 0;
  }, []);

  useEffect(() => {
    start(remainingRef.current);
    return () => clearTimeout(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const Icon = VARIANT_ICON[toast.variant];

  return (
    <div
      role="status"
      onMouseEnter={pause}
      onMouseLeave={() => start(remainingRef.current)}
      onFocus={pause}
      onBlur={() => start(remainingRef.current)}
      className={cn(
        "pointer-events-auto flex min-w-[240px] max-w-full items-start gap-3 rounded-[10px] border px-4 py-3 text-[14px] font-medium shadow-elev-lg",
        VARIANT_CLS[toast.variant] ?? VARIANT_CLS.default,
      )}
      data-testid={`toast-${toast.variant}`}
    >
      {Icon && (
        <Icon className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={2} aria-hidden />
      )}
      <div className="flex-1">{toast.message}</div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss notification"
        className="mt-0.5 -mr-1 inline-flex h-6 w-6 items-center justify-center rounded-md hover:bg-white/10 transition-colors duration-fast ease-out"
      >
        <X className="h-3.5 w-3.5" strokeWidth={1.75} />
      </button>
    </div>
  );
}

export default ToastProvider;
